import { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef, type ReactNode } from 'react';
import { apiFetch, setAccessToken, getAccessToken, ApiError } from './api.js';

interface AuthState {
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  userId: string | null;
  orgId: string | null;
  orgName: string | null;
  userEmail: string | null;
  userFirstName: string | null;
  userLastName: string | null;
  userRole: string | null;
  isImpersonating: boolean;
  impersonatorId: string | null;
  impersonationSessionId: string | null;
}

interface AuthContextValue extends AuthState {
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  setSessionFromToken: (token: string, userInfo?: { firstName?: string | null; lastName?: string | null; email?: string }) => Promise<void>;
  startImpersonation: (token: string) => void;
  stopImpersonation: () => void;
}

const defaultState: AuthState = {
  isAuthenticated: false,
  isLoading: true,
  error: null,
  userId: null,
  orgId: null,
  orgName: null,
  userEmail: null,
  userFirstName: null,
  userLastName: null,
  userRole: null,
  isImpersonating: false,
  impersonatorId: null,
  impersonationSessionId: null,
};

const AuthContext = createContext<AuthContextValue | null>(null);

const decodeToken = (token: string): {
  sub?: string;
  email?: string;
  role?: string;
  org_id?: string;
  impersonator_id?: string;
  impersonation_session_id?: string;
} => {
  try {
    const parts = token.split('.');
    const payload = JSON.parse(atob(parts[1] ?? ''));
    return {
      sub: payload.sub as string | undefined,
      email: payload.email as string | undefined,
      role: payload.role as string | undefined,
      org_id: payload.org_id as string | undefined,
      impersonator_id: payload.impersonator_id as string | undefined,
      impersonation_session_id: payload.impersonation_session_id as string | undefined,
    };
  } catch { return {}; }
};

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>(defaultState);
  const originalTokenRef = useRef<string | null>(null);

  // Try to refresh token on mount
  useEffect(() => {
    apiFetch<{ access_token: string; user?: { email?: string; firstName?: string | null; lastName?: string | null } }>('/auth/refresh', { method: 'POST' })
      .then(async (data) => {
        setAccessToken(data.access_token);
        const decoded = decodeToken(data.access_token);
        let orgName: string | null = null;
        if (decoded.org_id) {
          try {
            const orgData = await apiFetch<{ name: string }>(`/orgs/${decoded.org_id}`);
            orgName = orgData.name;
          } catch { /* */ }
        }
        setState({
          isAuthenticated: true,
          isLoading: false,
          error: null,
          userId: decoded.sub ?? null,
          orgId: decoded.org_id ?? null,
          orgName,
          userEmail: data.user?.email ?? decoded.email ?? null,
          userFirstName: data.user?.firstName ?? null,
          userLastName: data.user?.lastName ?? null,
          userRole: decoded.role ?? null,
          isImpersonating: !!decoded.impersonator_id,
          impersonatorId: decoded.impersonator_id ?? null,
          impersonationSessionId: decoded.impersonation_session_id ?? null,
        });
      })
      .catch(() => {
        setState({ ...defaultState, isLoading: false });
      });
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    setState(s => ({ ...s, error: null, isLoading: true }));
    try {
      const data = await apiFetch<{ access_token: string; user?: { email?: string; firstName?: string | null; lastName?: string | null } }>('/auth/login', {
        method: 'POST',
        body: JSON.stringify({ email, password }),
      });
      setAccessToken(data.access_token);
      const decoded = decodeToken(data.access_token);
      let orgName: string | null = null;
      try {
        if (decoded.org_id) {
          const orgData = await apiFetch<{ name: string }>(`/orgs/${decoded.org_id}`);
          orgName = orgData.name;
        } else {
          orgName = 'Admin';
        }
      } catch { /* */ }
      setState({
        isAuthenticated: true,
        isLoading: false,
        error: null,
        userId: decoded.sub ?? null,
        orgId: decoded.org_id ?? null,
        orgName,
        userEmail: email,
        userFirstName: data.user?.firstName ?? null,
        userLastName: data.user?.lastName ?? null,
        userRole: decoded.role ?? null,
        isImpersonating: false,
        impersonatorId: null,
        impersonationSessionId: null,
      });
    } catch (err) {
      const message = err instanceof ApiError
        ? err.message
        : 'An unexpected error occurred';
      setState({ ...defaultState, isLoading: false, error: message });
      throw err;
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await apiFetch('/auth/logout', { method: 'POST' });
    } catch {
      // Logout even if API call fails
    }
    setAccessToken(null);
    originalTokenRef.current = null;
    setState({ ...defaultState, isLoading: false });
  }, []);

  const setSessionFromToken = useCallback(async (token: string, userInfo?: { firstName?: string | null; lastName?: string | null; email?: string }) => {
    setAccessToken(token);
    const decoded = decodeToken(token);
    let orgName: string | null = null;
    if (decoded.org_id) {
      try {
        const orgData = await apiFetch<{ name: string }>(`/orgs/${decoded.org_id}`);
        orgName = orgData.name;
      } catch { /* */ }
    }
    setState({
      isAuthenticated: true,
      isLoading: false,
      error: null,
      userId: decoded.sub ?? null,
      orgId: decoded.org_id ?? null,
      orgName,
      userEmail: userInfo?.email ?? decoded.email ?? null,
      userFirstName: userInfo?.firstName ?? null,
      userLastName: userInfo?.lastName ?? null,
      userRole: decoded.role ?? null,
      isImpersonating: false,
      impersonatorId: null,
      impersonationSessionId: null,
    });
  }, []);

  const startImpersonation = useCallback((token: string) => {
    // Store the current (super-admin) token so we can restore it later
    originalTokenRef.current = getAccessToken();
    setAccessToken(token);
    const decoded = decodeToken(token);
    setState(s => ({
      ...s,
      userId: decoded.sub ?? null,
      orgId: decoded.org_id ?? null,
      userEmail: decoded.email ?? null,
      userFirstName: null,
      userLastName: null,
      userRole: decoded.role ?? null,
      isImpersonating: true,
      impersonatorId: decoded.impersonator_id ?? null,
      impersonationSessionId: decoded.impersonation_session_id ?? null,
    }));
  }, []);

  const stopImpersonation = useCallback(() => {
    const original = originalTokenRef.current;
    if (!original) return;
    originalTokenRef.current = null;
    setAccessToken(original);
    const decoded = decodeToken(original);
    setState(s => ({
      ...s,
      userId: decoded.sub ?? null,
      orgId: decoded.org_id ?? null,
      userEmail: decoded.email ?? null,
      userFirstName: null,
      userLastName: null,
      userRole: decoded.role ?? null,
      isImpersonating: false,
      impersonatorId: null,
      impersonationSessionId: null,
    }));
  }, []);

  const value = useMemo(
    () => ({ ...state, login, logout, setSessionFromToken, startImpersonation, stopImpersonation }),
    [state, login, logout, setSessionFromToken, startImpersonation, stopImpersonation],
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
